/**
 * Promo Engine — PegasoLux (ADR-010)
 *
 * Reads the JSON config emitted by snippets/promo-engine.liquid (active `promo_popup`
 * metaobject entries), decides which promotion the visitor is eligible for, renders the
 * popup, captures the email through Shopify's native /contact customer form, and delivers
 * the discount code on screen.
 *
 * Data contract (deliberately neutral): the only writes are a Shopify customer with
 * marketing consent + tags (promo:/src:/camp:/med:/cons:), dataLayer + Meta Pixel events, and
 * localStorage state for UTM memory, per-promo frequency and per-promo opt-out (the tab's
 * close button), plus a sessionStorage flag so a popup fires at most once per browser
 * session. No external requests.
 *
 * URL flags (operator/QA only):
 *   ?promo_preview=<handle>  force-show that ACTIVE promo (badge, no events, no state)
 *   &pe_trigger=1            preview honors the configured trigger instead of showing at once
 *   &pe_track=1              preview fires real tracking events (QA of tracking only)
 *   ?promo_debug=1           log the eligibility evaluation of every promo (and the resolved
 *                            reopen-tab design: parsed pestana_diseno, ignored keys, final
 *                            position after floating-element avoidance), render nothing extra
 *   &pe_estado=cerrado|convertido  with promo_preview: simulate the dismissed/converted
 *                            states to QA the reopen tab and code-recovery views
 */
(function () {
  'use strict';

  var VERSION = '1.7.0';
  var UTM_KEY = 'pe_utm';
  var UTM_TTL_DAYS = 30;
  var STATE_PREFIX = 'pe_p_';
  var MOBILE_QUERY = '(max-width: 749px)';
  var UTM_KEYS = ['source', 'medium', 'campaign', 'content', 'term'];

  var TXT = {
    emailPlaceholder: 'Tu correo electrónico',
    emailInvalid: 'Escribe un correo válido.',
    submitDefault: 'Obtener mi código',
    sending: 'Enviando…',
    submitError: 'No pudimos registrar tu correo. Inténtalo de nuevo.',
    successDefault: '¡Listo! Aquí está tu código:',
    copy: 'Copiar',
    copied: 'Copiado ✓',
    apply: 'Aplicar a mi compra',
    close: 'Cerrar',
    previewBadge: 'Vista previa',
    legalLink: 'política de privacidad',
    uniqueCodePending: 'Código único: proveedor v2 no configurado'
  };

  // Consent notice shown under the CTA (v1.7.0). Submitting the form with this notice
  // visible is the act of consent; the customer tag cons:<version> records which exact
  // wording the visitor saw. The literal is versioned here on purpose (never only in the
  // editable promo fields): any change to `text` or `link` must bump `version`.
  var CONSENT = {
    version: 'v1',
    text: 'Al registrarte, autorizas a Pegasolux a enviarte comunicaciones comerciales por correo electrónico. Consulta nuestra',
    link: 'Política de Privacidad'
  };

  /* ---------------------------------------------------------------- utils */

  function safeParse(raw) {
    try { return JSON.parse(raw); } catch (e) { return null; }
  }
  var store = {
    get: function (k) {
      try { return safeParse(localStorage.getItem(k)); } catch (e) { return null; }
    },
    set: function (k, v) {
      try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ }
    }
  };
  // Once-per-session gate: the popup auto-fires at most once per browser session
  // (tab/window), whether or not it was closed; frecuencia_dias governs the silence
  // between sessions after a close. Preview never writes it.
  var session = {
    has: function (handle) {
      try { return !!sessionStorage.getItem('pe_s_' + handle); } catch (e) { return false; }
    },
    mark: function (handle) {
      if (isPreview) return;
      try { sessionStorage.setItem('pe_s_' + handle, '1'); } catch (e) { /* storage blocked */ }
    }
  };
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function sanitizeTag(v) {
    return String(v || '').toLowerCase().slice(0, 60).replace(/[^a-z0-9._:-]+/g, '-');
  }
  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  /* ---------------------------------------------------------------- config */

  var cfgEl = document.getElementById('promo-engine-config');
  if (!cfgEl) return;
  var cfg = safeParse(cfgEl.textContent);
  if (!cfg || !cfg.promos || !cfg.promos.length) return;

  var params = new URLSearchParams(location.search);
  var previewHandle = params.get('promo_preview');
  var debugMode = params.get('promo_debug') === '1';
  var previewRealTrigger = params.get('pe_trigger') === '1';
  var previewRealTrack = params.get('pe_track') === '1';
  var isPreview = !!previewHandle;

  /* ------------------------------------------------------------- UTM memory */

  var nowMs = Date.now();
  var incoming = {};
  UTM_KEYS.forEach(function (k) {
    var v = params.get('utm_' + k);
    if (v) incoming[k] = v.toLowerCase().slice(0, 100);
  });
  var utmState = store.get(UTM_KEY);
  if (utmState && nowMs - (utmState.ts || 0) > UTM_TTL_DAYS * 864e5) utmState = null;
  if (Object.keys(incoming).length) {
    utmState = { first: (utmState && utmState.first) || incoming, last: incoming, ts: nowMs };
    store.set(UTM_KEY, utmState);
  }
  var origin = (utmState && utmState.last) || {};

  /* ------------------------------------------------------------ eligibility */

  function promoState(handle) {
    return store.get(STATE_PREFIX + handle) || {};
  }
  function setPromoState(handle, patch) {
    if (isPreview) return; // preview never writes frequency state
    var st = promoState(handle);
    for (var k in patch) st[k] = patch[k];
    store.set(STATE_PREFIX + handle, st);
  }

  // The tab's close button opts the visitor out of that promotion (popup and tab) in
  // this browser until the promotion's scheduled end; a new promotion starts clean.
  function optedOut(p) {
    var st = promoState(p.h);
    if (!st.optOutAt) return false;
    return !st.optOutUntil || Date.now() < st.optOutUntil * 1000;
  }

  function normalizePath(p) {
    p = (p || '/').toLowerCase();
    if (p.length > 1 && p.charAt(p.length - 1) === '/') p = p.slice(0, -1);
    return p;
  }
  function pathMatches(pattern, path) {
    pattern = normalizePath(pattern);
    if (pattern.slice(-2) === '/*') {
      var prefix = pattern.slice(0, -2);
      return path === prefix || path.indexOf(prefix + '/') === 0;
    }
    return path === pattern;
  }
  function ruleMatches(rule) {
    // "source=facebook,instagram" → origin.source must equal one of the values
    var parts = String(rule || '').split('=');
    if (parts.length !== 2) return true; // malformed rule: ignore rather than block
    var key = parts[0].trim().toLowerCase().replace(/^utm_/, '');
    if (UTM_KEYS.indexOf(key) === -1) return true;
    var actual = (origin[key] || '').toLowerCase();
    if (!actual) return false;
    return parts[1].split(',').some(function (v) { return v.trim().toLowerCase() === actual; });
  }

  function evaluate(p) {
    var t = Date.now();
    var checks = {};
    var st = promoState(p.h);
    checks.codigo = p.tc !== 'unico'; // v2: unique-code provider not available yet
    checks.fechas = (!p.ini || t >= p.ini * 1000) && (!p.fin || t <= p.fin * 1000);
    checks.dispositivo = p.dv === 'movil' ? isMobile() : p.dv === 'desktop' ? !isMobile() : true;
    checks.pagina = !(p.pg && p.pg.length) || p.pg.some(function (pat) {
      return pathMatches(pat, normalizePath(location.pathname));
    });
    checks.convertido = !st.converted;
    checks.frecuencia = !(st.dismissedAt && p.fq > 0 && t - st.dismissedAt < p.fq * 864e5);
    checks.sesion = isPreview || !session.has(p.h);
    checks.optout = !optedOut(p);
    checks.utm = !(p.ru && p.ru.length) || p.ru.every(ruleMatches);
    var ok = true;
    for (var k in checks) if (!checks[k]) ok = false;
    return { promo: p, ok: ok, checks: checks, specificity: (p.ru || []).length };
  }

  // Second pass: a promo blocked only by frequency/conversion can still offer its
  // reopen tab (every other rule — dates, device, pages, UTM — must still pass).
  function chooseTab() {
    var eligible = [];
    cfg.promos.forEach(function (p) {
      if (!tabAllowed(p)) return;
      var r = evaluate(p);
      if (r.checks.codigo && r.checks.fechas && r.checks.dispositivo && r.checks.pagina && r.checks.utm && r.checks.optout) {
        eligible.push({ promo: p, specificity: r.specificity });
      }
    });
    eligible.sort(function (a, b) {
      return (b.promo.pr || 10) - (a.promo.pr || 10) || b.specificity - a.specificity;
    });
    return eligible.length ? eligible[0].promo : null;
  }

  function choose() {
    var results = cfg.promos.map(evaluate);
    if (debugMode) {
      try {
        console.groupCollapsed('[promo-engine] evaluación v' + VERSION);
        console.log('origen UTM recordado:', origin);
        console.table(results.map(function (r) {
          var row = { promo: r.promo.h, elegible: r.ok, prioridad: r.promo.pr || 10, reglas: r.specificity };
          for (var k in r.checks) row[k] = r.checks[k];
          return row;
        }));
        console.groupEnd();
      } catch (e) { /* console unavailable */ }
    }
    var eligible = results.filter(function (r) { return r.ok; });
    eligible.sort(function (a, b) {
      return (b.promo.pr || 10) - (a.promo.pr || 10) || b.specificity - a.specificity;
    });
    return eligible.length ? eligible[0].promo : null;
  }

  /* --------------------------------------------------------------- tracking */

  function track(name, promo, extra) {
    var payload = { event: name, promo: promo.h, src: origin.source || 'directo', camp: origin.campaign || '' };
    if (extra) for (var k in extra) payload[k] = extra[k];
    if (isPreview && !previewRealTrack) {
      try { console.info('[promo-engine preview] evento suprimido:', name, payload); } catch (e) {}
      return;
    }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) { /* noop */ }
    try {
      if (typeof window.fbq === 'function') {
        var custom = { pl_popup_view: 'PL_PopupView', pl_popup_engage: 'PL_PopupEngage', pl_popup_lead: 'PL_PopupLead', pl_popup_close: 'PL_PopupClose', pl_popup_reopen: 'PL_PopupReopen', pl_popup_tab_close: 'PL_PopupTabClose' }[name];
        if (name === 'pl_popup_lead') {
          window.fbq('track', 'Lead', { content_name: promo.h, content_category: 'promo_popup' });
        }
        if (custom) window.fbq('trackCustom', custom, payload);
      }
    } catch (e) { /* noop */ }
  }

  /* ----------------------------------------------------------------- render */

  var root = null;
  var lastFocused = null;
  var scrollLocked = false;

  function lockScroll() {
    if (scrollLocked) return;
    scrollLocked = true;
    document.documentElement.style.setProperty('overflow', 'hidden');
  }
  function unlockScroll() {
    if (!scrollLocked) return;
    scrollLocked = false;
    document.documentElement.style.removeProperty('overflow');
  }

  function getCode(p) {
    if (p.tc === 'unico') return null; // v2 provider seam: fetch a one-time code here
    return p.co || null;
  }

  function legalNode(p) {
    var wrap = el('p', 'pe-legal');
    wrap.appendChild(document.createTextNode(p.le + ' '));
    if (cfg.legalUrl) {
      var a = el('a', null, TXT.legalLink);
      a.href = cfg.legalUrl;
      a.target = '_blank';
      a.rel = 'noopener';
      wrap.appendChild(a);
      wrap.appendChild(document.createTextNode('.'));
    }
    return wrap;
  }

  // Consent notice (same typography as the legal line). Returns null when the shop has
  // no published privacy policy: without its link the notice is not shown and, in turn,
  // buildTags() does not send the cons: tag.
  function consentNode() {
    if (!cfg.legalUrl) return null;
    var wrap = el('p', 'pe-legal pe-consent');
    wrap.appendChild(document.createTextNode(CONSENT.text + ' '));
    var a = el('a', null, CONSENT.link);
    a.href = cfg.legalUrl;
    a.target = '_blank';
    a.rel = 'noopener';
    wrap.appendChild(a);
    wrap.appendChild(document.createTextNode('.'));
    return wrap;
  }

  // hCaptcha attribution line. Its data-spam-detection-disclaimer attribute is the
  // official switch that stops Shopify's captcha module from rendering its floating
  // "protected by hCaptcha" badge (the module checks for this element before painting).
  function hcaptchaDisclaimer() {
    var prev = document.getElementById('pe-hcaptcha-disclaimer');
    if (prev && prev.parentNode) prev.parentNode.removeChild(prev);
    var pEl = el('p', 'pe-legal');
    pEl.id = 'pe-hcaptcha-disclaimer';
    pEl.setAttribute('data-spam-detection-disclaimer', '');
    pEl.appendChild(document.createTextNode('Este sitio está protegido por hCaptcha; aplican su '));
    var a1 = el('a', null, 'Política de Privacidad');
    a1.href = 'https://hcaptcha.com/privacy';
    a1.target = '_blank';
    a1.rel = 'noopener';
    pEl.appendChild(a1);
    pEl.appendChild(document.createTextNode(' y sus '));
    var a2 = el('a', null, 'Condiciones de Servicio');
    a2.href = 'https://hcaptcha.com/terms';
    a2.target = '_blank';
    a2.rel = 'noopener';
    pEl.appendChild(a2);
    pEl.appendChild(document.createTextNode('.'));
    return pEl;
  }

  function codeBlock(p) {
    // Prefer the code persisted at delivery time (future-proof for unique codes v2).
    var code = promoState(p.h).code || getCode(p);
    var wrap = el('div', 'pe-code-wrap');
    if (!code) {
      wrap.appendChild(el('p', 'pe-code-pending', TXT.uniqueCodePending));
      return wrap;
    }
    var box = el('div', 'pe-code');
    box.appendChild(el('span', 'pe-code-value', code));
    var copyBtn = el('button', 'pe-copy', TXT.copy);
    copyBtn.type = 'button';
    copyBtn.addEventListener('click', function () {
      var done = function () { copyBtn.textContent = TXT.copied; setTimeout(function () { copyBtn.textContent = TXT.copy; }, 2200); };
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(done, done);
        } else { done(); }
      } catch (e) { done(); }
    });
    box.appendChild(copyBtn);
    wrap.appendChild(box);
    if (p.mc) {
      wrap.appendChild(el('p', 'pe-code-msg', p.mc.split('{codigo}').join(code)));
    }
    var apply = el('a', 'pe-apply', TXT.apply);
    apply.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(location.pathname || '/');
    wrap.appendChild(apply);
    return wrap;
  }

  function successView(p, body) {
    // Preserve the badge-suppressing attribution element before wiping the card body.
    var disc = body.querySelector('[data-spam-detection-disclaimer]');
    if (disc) {
      disc.hidden = true;
      document.body.appendChild(disc);
    }
    body.textContent = '';
    body.appendChild(el('h2', 'pe-title', p.me || TXT.successDefault));
    body.appendChild(codeBlock(p));
  }

  function returnToUrl(p) {
    var sp = new URLSearchParams(location.search);
    sp.set('pe_done', p.h);
    return location.pathname + '?' + sp.toString();
  }

  /* ------------------------------------------------------------- reopen tab */

  var tabEl = null;   // the reopen button
  var wrapEl = null;  // fixed wrapper: position, avoidance and reveal live here
  var tabQaConverted = false;
  var tabDesign = null; // resolved presentation of the current tab (QA/debug only)
  var tabAvoidBound = false;
  var TAB_VERT = ['arriba', 'centro', 'abajo'];
  var TAB_SIZES = ['pequena', 'mediana', 'grande'];
  var TAB_SHADOWS = ['ninguna', 'suave', 'media'];
  var TAB_ANIMS = ['sutil', 'fundido', 'ninguna'];
  var TAB_DESIGN_KEYS = ['posicion', 'posicion_movil', 'tamano_movil', 'color_borde', 'grosor_borde',
    'color_hover', 'radio', 'sombra', 'animacion', 'icono', 'evitar_flotantes'];

  function tabAllowed(p) {
    if (!p.tb) return false;
    if (optedOut(p)) return false;
    var dv = p.tdv || 'todos';
    if (dv === 'solo_desktop' && isMobile()) return false;
    if (dv === 'solo_movil' && !isMobile()) return false;
    return true;
  }

  // Vertical position: a keyword (arriba|centro|abajo) or "NN%" = where the CENTRE of
  // the tab sits, clamped to 10–90% so it never leaves the viewport. Anything else
  // falls back — the Admin schema already rejects malformed values at save time.
  function tabPosition(v, fallback) {
    var t = String(v == null ? '' : v).trim().toLowerCase();
    if (TAB_VERT.indexOf(t) !== -1) return t;
    var m = /^(\d{1,3})(?:\.\d+)?\s*%?$/.exec(t);
    if (!m) return fallback;
    return Math.min(90, Math.max(10, parseInt(m[1], 10))) + '%';
  }

  // pestana_diseno (metaobject json field) arrives already parsed — Liquid serialises it
  // with | json — a string is tolerated for robustness. Every key is optional; unknown
  // keys are ignored (listed under ?promo_debug=1), never fatal. Mobile overrides
  // (posicion_movil, tamano_movil) win on ≤749px; the dropdowns stay the desktop base.
  function tabDesignOf(p) {
    var raw = p.tds;
    if (typeof raw === 'string') raw = safeParse(raw);
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) raw = {};
    var ignored = [];
    for (var k in raw) if (TAB_DESIGN_KEYS.indexOf(k) === -1) ignored.push(k);
    var pick = function (v, list, def) { return list.indexOf(v) !== -1 ? v : def; };
    var cssLen = function (v, def) {
      var t = String(v == null ? '' : v).trim();
      return /^\d+(\.\d+)?(px|rem|em)$/.test(t) ? t : def;
    };
    var cssColor = function (v) {
      var t = String(v == null ? '' : v).trim();
      return t && t.length <= 40 && /^[#a-z0-9(),.%\s-]+$/i.test(t) ? t : '';
    };
    var mobile = isMobile();
    var ub = String(p.tub || 'derecha-centro').split('-');
    var baseVert = ub[1] === 'personalizada'
      ? tabPosition(raw.posicion, '60%')
      : (TAB_VERT.indexOf(ub[1]) !== -1 ? ub[1] : 'centro');
    var hasMobilePos = raw.posicion_movil != null && String(raw.posicion_movil).trim() !== '';
    var borderColor = cssColor(raw.color_borde);
    return {
      side: ub[0] === 'izquierda' ? 'izquierda' : 'derecha',
      vert: mobile && hasMobilePos ? tabPosition(raw.posicion_movil, baseVert) : baseVert,
      size: mobile && raw.tamano_movil ? pick(raw.tamano_movil, TAB_SIZES, 'pequena') : pick(p.tsz, TAB_SIZES, 'pequena'),
      borderColor: borderColor,
      borderWidth: cssLen(raw.grosor_borde, '1px'),
      hover: cssColor(raw.color_hover),
      radius: cssLen(raw.radio, '10px'),
      shadow: pick(raw.sombra, TAB_SHADOWS, 'suave'),
      anim: pick(raw.animacion, TAB_ANIMS, 'sutil'),
      icon: String(raw.icono || p.tic || '').trim().slice(0, 16),
      avoid: raw.evitar_flotantes !== false,
      ignored: ignored
    };
  }

  // Vertical placement never touches `transform` (the reveal animation owns it, and
  // prefers-reduced-motion forces it to none): keywords are classes on the wrapper,
  // percentages set --pe-tab-top and the CSS centres it with the independent `translate`.
  function placeTab(vert) {
    TAB_VERT.forEach(function (v) { wrapEl.classList.remove('pe-tab-wrap--' + v); });
    if (/%$/.test(vert)) {
      wrapEl.classList.add('pe-tab-wrap--pct');
      wrapEl.style.setProperty('--pe-tab-top', vert);
    } else {
      wrapEl.classList.remove('pe-tab-wrap--pct');
      wrapEl.style.removeProperty('--pe-tab-top');
      wrapEl.classList.add('pe-tab-wrap--' + vert);
    }
  }

  function ensureTab(p) {
    if (tabEl) return tabEl;
    var d = tabDesign = tabDesignOf(p);
    var label = p.tt || p.ti || '';

    wrapEl = el('div', 'pe-tab-wrap pe-tab-wrap--' + d.side);
    wrapEl.id = 'pe-tab-wrap';
    wrapEl.style.setProperty('--pe-tab-bg', p.tc1 || p.c3 || '#1e1c19');
    wrapEl.style.setProperty('--pe-tab-fg', p.tc2 || p.c4 || '#ffffff');
    wrapEl.style.setProperty('--pe-tab-radius', d.radius);

    // Close button: opts the visitor out of this promotion (tab and popup) in this
    // browser until the promotion ends. A separate element — a button cannot nest
    // inside the tab button — so it sits above the tab inside the fixed wrapper.
    var closeBtn = el('button', 'pe-tab-x');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Ocultar esta promoción');
    closeBtn.innerHTML = '&#215;';
    closeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      hideTab();
      var delivered = tabQaConverted || !!promoState(p.h).converted;
      setPromoState(p.h, { optOutAt: Date.now(), optOutUntil: p.fin || 0 });
      track('pl_popup_tab_close', p, { estado: delivered ? 'con_codigo' : 'sin_email' });
    });
    wrapEl.appendChild(closeBtn);

    tabEl = el('button', 'pe-tab');
    tabEl.type = 'button';
    tabEl.id = 'pe-tab';
    tabEl.setAttribute('aria-label', 'Abrir promoción: ' + label);
    tabEl.setAttribute('aria-expanded', 'false');
    if (d.icon) tabEl.appendChild(el('span', 'pe-tab-ico', d.icon));
    tabEl.appendChild(el('span', 'pe-tab-txt', label));
    tabEl.classList.add('pe-tab--' + d.side);
    tabEl.classList.add('pe-tab--' + d.size);
    tabEl.classList.add('pe-tab--sombra-' + d.shadow);
    tabEl.classList.add('pe-tab--anim-' + d.anim);
    if (d.borderColor) {
      tabEl.classList.add('pe-tab--borde');
      tabEl.style.setProperty('--pe-tab-border', d.borderWidth + ' solid ' + d.borderColor);
    }
    if (d.hover) {
      tabEl.classList.add('pe-tab--hover');
      tabEl.style.setProperty('--pe-tab-hover', d.hover);
    }
    tabEl.addEventListener('click', function () {
      if (root) return; // idempotent: a popup is already open
      hideTab();
      var delivered = tabQaConverted || !!promoState(p.h).converted;
      track('pl_popup_reopen', p, { estado: delivered ? 'con_codigo' : 'sin_email' });
      tabEl.setAttribute('aria-expanded', 'true');
      buildPopup(p, delivered);
    });
    wrapEl.appendChild(tabEl);
    placeTab(d.vert);
    document.body.appendChild(wrapEl);
    return tabEl;
  }

  /* Floating-element avoidance (best effort, presentational only).
     Persistent floating UI on the same edge — WhatsApp button, cart FAB, chat widgets —
     is detected by hit-testing the tab's footprint; the tab is then nudged up/down in
     6% steps from its configured position and returns to it if no free slot exists
     (overlapping beats disappearing). Only --pe-tab-top / the position class change:
     the elements, their aria state, the reopen logic and tracking are never touched.
     Out of scope by design: drawers opened later, native browser UI. */

  function tabFixedAncestor(node) {
    var n = node;
    while (n && n !== document.body && n !== document.documentElement) {
      var pos = getComputedStyle(n).position;
      if (pos === 'fixed' || pos === 'sticky') return n;
      n = n.parentElement;
    }
    return null;
  }

  function tabIsEngineNode(e) {
    return e === wrapEl || wrapEl.contains(e) ||
      (e.id && e.id.indexOf('pe-') === 0) ||
      (typeof e.className === 'string' && e.className.indexOf('pe-') === 0);
  }

  // Resolves the floating element painted under (x, y) for one hit-test result, or
  // null. Shadow-DOM widgets (WhatsApp, chat bubbles) are returned by the document
  // hit-test as their host — usually a static, zero-height custom element — so the
  // open shadow tree is hit-tested again to find the fixed box that actually paints.
  function tabFloatingAt(e, x, y, W, H) {
    var target = e;
    var fixed = tabFixedAncestor(e);
    if (!fixed && e.shadowRoot) {
      var inner = [];
      try { inner = e.shadowRoot.elementsFromPoint(x, y); } catch (err) { inner = []; }
      for (var k = 0; k < inner.length; k++) {
        var n = inner[k];
        if (n.getRootNode && n.getRootNode() !== e.shadowRoot) continue;
        var f = tabFixedAncestor(n);
        if (f) { fixed = f; target = n; break; }
      }
    }
    if (!fixed && !e.shadowRoot && e.tagName.indexOf('-') !== -1) {
      // Closed shadow tree: the host was hit outside its own box, so something inside
      // paints there. Treat it as a floating control (cannot be measured further).
      var hr = e.getBoundingClientRect();
      if (x < hr.left || x > hr.right || y < hr.top || y > hr.bottom) return e;
    }
    if (!fixed) return null;
    var cs = getComputedStyle(target), fs = getComputedStyle(fixed);
    if (parseFloat(cs.opacity) < 0.05 || parseFloat(fs.opacity) < 0.05) return null;
    if (cs.visibility === 'hidden' || fs.visibility === 'hidden') return null;
    var er = target.getBoundingClientRect();
    if (er.width < 20 || er.height < 20) return null; // trivial box
    if (er.width >= W * 0.95 && er.height >= H * 0.95) return null; // full-viewport overlay, not a control
    return target;
  }

  function tabConflict() {
    var r = wrapEl.getBoundingClientRect();
    var W = window.innerWidth, H = window.innerHeight;
    // Dense grid over the tab's footprint (3 columns x a row every ~20px): an obstacle
    // smaller than the tab cannot slip between the sample points.
    var xs = [r.left + 2, (r.left + r.right) / 2, r.right - 2];
    var rows = Math.min(16, Math.max(2, Math.ceil(r.height / 20) + 1));
    for (var ri = 0; ri < rows; ri++) {
      var y = Math.min(H - 1, Math.max(0, r.top + 2 + (r.height - 4) * ri / (rows - 1)));
      for (var ci = 0; ci < xs.length; ci++) {
        var x = Math.min(W - 1, Math.max(0, xs[ci]));
        var hits = document.elementsFromPoint(x, y);
        for (var j = 0; j < hits.length; j++) {
          var e = hits[j];
          if (e === document.body || e === document.documentElement || tabIsEngineNode(e)) continue;
          var hit = tabFloatingAt(e, x, y, W, H);
          if (hit) return hit;
        }
      }
    }
    return null;
  }

  function tabSearchFree() {
    var H = window.innerHeight;
    var r0 = wrapEl.getBoundingClientRect();
    var base = (r0.top + r0.bottom) / 2 / H * 100;
    var half = r0.height / 2 / H * 100;
    var lo = Math.max(10, half + 1), hi = Math.min(90, 100 - half - 1);
    var steps = [-6, 6, -12, 12, -18, 18, -24, 24, -30, 30, -36, 36, -42, 42, -48, 48];
    for (var i = 0; i < steps.length; i++) {
      var c = Math.round(base + steps[i]);
      if (c < lo || c > hi) continue;
      placeTab(c + '%');
      if (!tabConflict()) return c + '%';
    }
    return tabDesign.vert;
  }

  function tabHot() {
    try { return wrapEl.matches(':hover') || wrapEl.contains(document.activeElement); } catch (e) { return false; }
  }

  function tabVisible() {
    return !!wrapEl && wrapEl.classList.contains('pe-tab-wrap--visible');
  }

  function avoidFloating(initial) {
    if (!wrapEl || !tabDesign || !tabDesign.avoid) return;
    if (!initial && (!tabVisible() || tabHot())) return;
    if (!tabConflict()) return; // the current spot is free: nothing moves
    var prevTransition = wrapEl.style.transition;
    wrapEl.style.transition = 'none'; // measure real geometry, not a mid-transition frame
    var current = wrapEl.getAttribute('data-pe-moved') || tabDesign.vert;
    placeTab(tabDesign.vert);
    var found = tabConflict() ? tabSearchFree() : tabDesign.vert;
    placeTab(current);
    void wrapEl.offsetHeight; // flush, so a late move animates from where the tab is
    wrapEl.style.transition = prevTransition;
    placeTab(found);
    if (found === tabDesign.vert) wrapEl.removeAttribute('data-pe-moved');
    else wrapEl.setAttribute('data-pe-moved', found);
  }

  function showTab(p, qaConverted) {
    if (!tabAllowed(p)) return;
    tabQaConverted = !!qaConverted;
    ensureTab(p);
    tabEl.setAttribute('aria-expanded', 'false');
    avoidFloating(true); // settle the position while still invisible: no visible jump
    if (!tabAvoidBound) {
      tabAvoidBound = true;
      var pending = null;
      var later = function () {
        clearTimeout(pending);
        pending = setTimeout(function () { avoidFloating(false); }, 250);
      };
      window.addEventListener('resize', later);
      window.addEventListener('orientationchange', later);
      setTimeout(function () { avoidFloating(false); }, 3000); // late-loading widgets
    }
    if (window.__promoEngine) window.__promoEngine.tabDesign = tabDesign;
    if (debugMode) {
      try {
        console.log('[promo-engine] pestaña', {
          diseno: tabDesign, clavesIgnoradas: tabDesign.ignored,
          posicionFinal: wrapEl.getAttribute('data-pe-moved') || tabDesign.vert
        });
      } catch (e) { /* console unavailable */ }
    }
    // rAF gives the CSS transition a starting frame, but it pauses on hidden or
    // backgrounded pages — the timeout guarantees the tab shows up regardless.
    var reveal = function () { wrapEl.classList.add('pe-tab-wrap--visible'); };
    requestAnimationFrame(reveal);
    setTimeout(reveal, 150);
  }

  function hideTab() {
    if (wrapEl) wrapEl.classList.remove('pe-tab-wrap--visible');
  }

  // Lead data travels as customer tags (the only channel the native customer form
  // offers). Public tags stay on the customer (newsletter, promo:, src:, camp:, med:,
  // cons:); pe-captura marks the record for the "PE · Lead capturado" Flow workflow,
  // which moves the transient tags (cod:, pg:, dev:, ts:) into promo_engine.* customer
  // metafields and removes them together with the marker. cons:<version> is sent only
  // when the consent notice is rendered in the popup (consentShown).
  function buildTags(p, consentShown) {
    var tags = ['newsletter', 'pe-captura', 'promo:' + sanitizeTag(p.h), 'src:' + sanitizeTag(origin.source || 'directo')];
    if (consentShown) tags.push('cons:' + sanitizeTag(CONSENT.version));
    if (origin.campaign) tags.push('camp:' + sanitizeTag(origin.campaign));
    if (origin.medium) tags.push('med:' + sanitizeTag(origin.medium));
    var code = getCode(p);
    if (code) tags.push('cod:' + sanitizeTag(code));
    tags.push('pg:' + (sanitizeTag(location.pathname.replace(/^\/+|\/+$/g, '')) || 'inicio'));
    tags.push('dev:' + (isMobile() ? 'movil' : 'desktop'));
    tags.push('ts:' + new Date().toISOString().slice(0, 19).replace(/:/g, '-'));
    return tags;
  }

  function formFallback(body) {
    var f = document.createElement('form');
    f.method = 'POST';
    f.action = '/contact';
    body.forEach(function (v, k) {
      var i = document.createElement('input');
      i.type = 'hidden'; i.name = k; i.value = v;
      f.appendChild(i);
    });
    document.body.appendChild(f);
    f.submit();
  }

  function submitEmail(p, form, input, button, errEl) {
    var email = (input.value || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      errEl.textContent = TXT.emailInvalid;
      errEl.hidden = false;
      input.focus();
      return;
    }
    errEl.hidden = true;
    button.disabled = true;
    var originalLabel = button.textContent;
    button.textContent = TXT.sending;

    var body = new URLSearchParams(new FormData(form));

    fetch('/contact', { method: 'POST', body: body, headers: { Accept: 'text/html' } })
      .then(function (res) {
        if (res.status === 400 || (res.url && res.url.indexOf('challenge') !== -1)) {
          // Store-level spam protection (captcha) rejects AJAX posts. Fall back to a real
          // form navigation: Shopify serves its challenge page, then honors return_to,
          // which brings the visitor back with ?pe_done=<handle> to deliver the code.
          formFallback(body);
          return;
        }
        if (!res.ok && !res.redirected) throw new Error('status ' + res.status);
        setPromoState(p.h, { converted: true, convertedAt: Date.now(), code: getCode(p) });
        track('pl_popup_lead', p);
        successView(p, form.closest('.pe-body'));
      })
      .catch(function () {
        button.disabled = false;
        button.textContent = originalLabel;
        errEl.textContent = TXT.submitError;
        errEl.hidden = false;
      });
  }

  function buildPopup(p, showSuccess) {
    hideTab(); // the tab never coexists with an open popup
    session.mark(p.h);
    var style = p.st || 'modal_central';
    var pos = p.po && p.po !== 'auto' ? p.po : (style === 'panel_lateral' ? 'derecha' : 'abajo');
    var anim = p.an || 'fundido';
    var modal = style === 'modal_central';

    root = el('div', 'pe-root pe-style-' + style + ' pe-pos-' + pos + ' pe-anim-' + anim);
    if (p.c1) root.style.setProperty('--pe-bg', p.c1);
    if (p.c2) root.style.setProperty('--pe-fg', p.c2);
    if (p.c3) root.style.setProperty('--pe-btn', p.c3);
    if (p.c4) root.style.setProperty('--pe-btn-fg', p.c4);

    if (modal) {
      var overlay = el('div', 'pe-overlay');
      overlay.addEventListener('click', function () { closePopup(p, 'overlay'); });
      root.appendChild(overlay);
    }

    var card = el('div', 'pe-card');
    card.setAttribute('role', 'dialog');
    if (modal) card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-labelledby', 'pe-title-' + p.h);
    if (p.bg) {
      card.classList.add('pe-card--bgimg');
      card.style.backgroundImage = 'url("' + p.bg + '")';
    }

    var close = el('button', 'pe-close');
    close.type = 'button';
    close.setAttribute('aria-label', TXT.close);
    close.innerHTML = '&#215;';
    close.addEventListener('click', function () { closePopup(p, 'boton'); });
    card.appendChild(close);

    // Device-specific creative: the snippet resolves the desktop/mobile slots (with
    // cross-fallback) in Liquid; here we only pick the slot for the current viewport.
    var slotU = isMobile() ? (p.immu || p.im) : (p.imdu || p.im);
    var slotU2 = isMobile() ? p.immu2 : p.imdu2;
    var slotR = isMobile() ? (p.immr || 0) : (p.imdr || 0);
    if (slotU && !p.bg) {
      var media = el('div', 'pe-media');
      if (slotR > 0) {
        // Reserve the creative's intrinsic ratio before it loads: no internal shift,
        // and the image renders complete (extreme creatives shrink, never crop).
        media.style.aspectRatio = String(slotR);
        media.classList.add('pe-media--ratio');
      }
      var img = document.createElement('img');
      img.src = slotU;
      if (slotU2) img.srcset = slotU + ' 1x, ' + slotU2 + ' 2x';
      img.alt = '';
      img.loading = 'eager';
      img.decoding = 'async';
      media.appendChild(img);
      card.appendChild(media);
      card.classList.add('pe-card--media');
    }

    var body = el('div', 'pe-body');
    var title = el('h2', 'pe-title', p.ti || '');
    title.id = 'pe-title-' + p.h;
    body.appendChild(title);
    if (p.su) body.appendChild(el('p', 'pe-sub', p.su));
    if (p.de) body.appendChild(el('p', 'pe-desc', p.de));

    var requiresEmail = p.re !== false;
    if (requiresEmail && !showSuccess) {
      // A genuine Shopify customer form (action + form_type) so the store's captcha
      // wiring recognizes it; Shopify.captcha.protect() attaches the token on submit.
      var captchaMode = !!(window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function');
      var form = el('form', 'pe-form');
      form.setAttribute('method', 'post');
      form.setAttribute('action', '/contact');
      form.setAttribute('accept-charset', 'UTF-8');
      if (!captchaMode) form.noValidate = true;
      var hidden = function (name, value) {
        var i = document.createElement('input');
        i.type = 'hidden'; i.name = name; i.value = value;
        form.appendChild(i);
      };
      hidden('form_type', 'customer');
      hidden('utf8', '✓');
      var consentEl = consentNode();
      hidden('contact[tags]', buildTags(p, !!consentEl).join(','));
      hidden('return_to', returnToUrl(p));
      var label = el('label', 'pe-visually-hidden', TXT.emailPlaceholder);
      label.htmlFor = 'pe-email-' + p.h;
      var input = document.createElement('input');
      input.type = 'email';
      input.id = 'pe-email-' + p.h;
      input.className = 'pe-input';
      input.placeholder = TXT.emailPlaceholder;
      input.autocomplete = 'email';
      input.required = true;
      input.name = 'contact[email]';
      var button = el('button', 'pe-btn', p.bt || TXT.submitDefault);
      button.type = 'submit';
      var errEl = el('p', 'pe-error');
      errEl.hidden = true;
      errEl.setAttribute('role', 'alert');
      form.appendChild(label);
      form.appendChild(input);
      form.appendChild(button);
      body.appendChild(form);
      body.appendChild(errEl);
      // Consent notice under the CTA (null only without a privacy policy URL).
      if (consentEl) body.appendChild(consentEl);
      // Optional extra legal line: renders only when the promotion's texto_legal
      // field has content (merchant decision, no code involved).
      if (p.le) body.appendChild(legalNode(p));
      var discEl = null;
      if (captchaMode) {
        // Must exist in the DOM from the start (it suppresses the floating hCaptcha
        // badge), but it is only shown while the invisible verification actually runs.
        discEl = hcaptchaDisclaimer();
        discEl.hidden = true;
        body.appendChild(discEl);
      }

      var engaged = false;
      var engage = function () {
        if (engaged) return;
        engaged = true;
        track('pl_popup_engage', p);
      };
      input.addEventListener('focus', engage, { once: true });
      var waitingBind = false;
      var bindTimedOut = false;
      var watchdog = null;
      var restoreButton = function () {
        button.disabled = false;
        button.textContent = p.bt || TXT.submitDefault;
      };
      form.addEventListener('submit', function (e) {
        engage();
        if (captchaMode) {
          button.disabled = true;
          button.textContent = TXT.sending;
          // Attribution becomes visible exactly while hCaptcha verifies the submission.
          if (discEl) discEl.hidden = false;
          if (form.hasAttribute('data-hcaptcha-bound') || bindTimedOut) {
            // Once Shopify's captcha wiring is bound it intercepts this submit, runs the
            // invisible check and re-submits with the h-captcha-response token attached —
            // no challenge page. If it silently fails (network error, an abandoned
            // interactive challenge), hand the button back so the visitor can retry.
            if (watchdog) clearTimeout(watchdog);
            watchdog = setTimeout(function () {
              restoreButton();
              errEl.textContent = TXT.submitError;
              errEl.hidden = false;
            }, 30000);
            return;
          }
          // Wiring not bound yet: hold the submit until it binds. If it never binds
          // (blocked script), submit anyway and let the server-side challenge handle it.
          e.preventDefault();
          if (waitingBind) return;
          waitingBind = true;
          var waited = 0;
          var tick = setInterval(function () {
            waited += 150;
            if (form.hasAttribute('data-hcaptcha-bound') || waited >= 5000) {
              clearInterval(tick);
              waitingBind = false;
              bindTimedOut = !form.hasAttribute('data-hcaptcha-bound');
              // Restore first: if constraint validation blocks requestSubmit, the
              // visitor keeps a live button; a proceeding submit re-disables it.
              restoreButton();
              if (form.requestSubmit) form.requestSubmit(); else form.submit();
            }
          }, 150);
          return;
        }
        e.preventDefault();
        submitEmail(p, form, input, button, errEl);
      });
      if (captchaMode) {
        try { window.Shopify.captcha.protect(form); } catch (e) { /* noop */ }
      }
    } else if (!showSuccess) {
      body.appendChild(codeBlock(p));
      card.addEventListener('click', function fn(e) {
        if (e.target.closest('.pe-apply, .pe-copy')) {
          track('pl_popup_engage', p);
          card.removeEventListener('click', fn);
        }
      });
    }

    card.appendChild(body);
    root.appendChild(card);

    if (isPreview) {
      var badge = el('div', 'pe-preview-badge', TXT.previewBadge + ' · ' + p.h);
      root.appendChild(badge);
    }

    document.body.appendChild(root);
    if (modal) lockScroll();

    lastFocused = document.activeElement;
    requestAnimationFrame(function () {
      root.classList.add('pe-open');
      var focusable = card.querySelector('.pe-input, .pe-apply, .pe-close');
      if (focusable) focusable.focus({ preventScroll: true });
    });

    document.addEventListener('keydown', keyHandler);
    function keyHandler(e) {
      if (!root) { document.removeEventListener('keydown', keyHandler); return; }
      if (e.key === 'Escape') { closePopup(p, 'escape'); return; }
      if (modal && e.key === 'Tab') {
        var items = card.querySelectorAll('button, a[href], input');
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    setPromoState(p.h, { shownAt: Date.now() });
    if (showSuccess) {
      successView(p, body);
    } else {
      track('pl_popup_view', p);
    }
  }

  function closePopup(p, how) {
    if (!root) return;
    var node = root;
    root = null;
    // Keep the hCaptcha attribution element alive as a hidden sentinel: if the captcha
    // module finishes loading after the popup closes, its badge check still finds it
    // and the floating badge is never painted.
    var disc = node.querySelector('[data-spam-detection-disclaimer]');
    if (disc) {
      disc.hidden = true;
      document.body.appendChild(disc);
    }
    node.classList.remove('pe-open');
    unlockScroll();
    setPromoState(p.h, { dismissedAt: Date.now() });
    track('pl_popup_close', p, { how: how });
    // The reopen tab returns right away (before focus restore, so a keyboard user
    // who reopened via the tab lands back on the visible tab).
    if (p.tb) showTab(p, tabQaConverted);
    setTimeout(function () {
      if (node.parentNode) node.parentNode.removeChild(node);
    }, 350);
    try { if (lastFocused && lastFocused.focus) lastFocused.focus({ preventScroll: true }); } catch (e) {}
  }

  /* ---------------------------------------------------------------- trigger */

  function installTrigger(p) {
    var type = p.tr || 'retardo';
    var value = typeof p.tv === 'number' ? p.tv : parseInt(p.tv, 10);
    var fired = false;
    var fire = function () {
      if (fired || root) return;
      fired = true;
      buildPopup(p);
    };

    if (isPreview && !previewRealTrigger) {
      setTimeout(fire, 350);
      return;
    }
    if (type === 'scroll') {
      var pct = isNaN(value) ? 40 : Math.min(Math.max(value, 1), 100);
      var onScroll = function () {
        var max = document.documentElement.scrollHeight - window.innerHeight;
        if (max <= 0) return fireAndClean();
        if ((window.scrollY / max) * 100 >= pct) fireAndClean();
      };
      var fireAndClean = function () {
        window.removeEventListener('scroll', onScroll);
        fire();
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      return;
    }
    if (type === 'salida' && !isMobile()) {
      var onOut = function (e) {
        if (!e.relatedTarget && e.clientY <= 0) {
          document.removeEventListener('mouseout', onOut);
          fire();
        }
      };
      document.addEventListener('mouseout', onOut);
      return;
    }
    // retardo — also the mobile fallback for "salida"
    var seconds = isNaN(value) ? 5 : Math.max(type === 'salida' ? Math.max(value || 0, 8) : value, 1);
    setTimeout(fire, seconds * 1000);
  }

  /* ------------------------------------------------------------------- boot */

  function boot() {
    // Return leg of the captcha-protected submit: deliver the code and record the lead.
    var doneHandle = params.get('pe_done');
    if (doneHandle) {
      var done = null;
      for (var j = 0; j < cfg.promos.length; j++) {
        if (cfg.promos[j].h === doneHandle) { done = cfg.promos[j]; break; }
      }
      if (done) {
        var wasConverted = promoState(done.h).converted;
        setPromoState(done.h, { converted: true, convertedAt: Date.now(), code: getCode(done) });
        if (!wasConverted) track('pl_popup_lead', done);
        try {
          var clean = new URLSearchParams(location.search);
          clean.delete('pe_done');
          var qs = clean.toString();
          history.replaceState(null, '', location.pathname + (qs ? '?' + qs : ''));
        } catch (e) { /* noop */ }
        window.__promoEngine = { version: VERSION, config: cfg, origin: origin, chosen: done.h, preview: isPreview, delivered: true };
        buildPopup(done, true);
        return;
      }
    }

    var chosen = null;
    if (previewHandle) {
      for (var i = 0; i < cfg.promos.length; i++) {
        if (cfg.promos[i].h === previewHandle) { chosen = cfg.promos[i]; break; }
      }
      if (!chosen) {
        try { console.warn('[promo-engine] promo_preview: no hay entrada ACTIVE con handle "' + previewHandle + '"'); } catch (e) {}
      }
      // QA flag: simulate the dismissed/converted states (tab instead of popup),
      // without writing state or firing events.
      var qaEstado = params.get('pe_estado');
      if (chosen && (qaEstado === 'cerrado' || qaEstado === 'convertido')) {
        window.__promoEngine = { version: VERSION, config: cfg, origin: origin, chosen: null, tab: chosen.h, preview: true, qaEstado: qaEstado };
        showTab(chosen, qaEstado === 'convertido');
        return;
      }
    } else {
      chosen = choose();
    }
    window.__promoEngine = { version: VERSION, config: cfg, origin: origin, chosen: chosen && chosen.h, preview: isPreview };
    if (!chosen) {
      var tp = chooseTab();
      window.__promoEngine.tab = tp && tp.h;
      if (tp) showTab(tp);
      return;
    }
    // If this promo was dismissed before and has a tab, keep the tab visible while
    // the trigger is armed: the affordance never blinks out between sessions.
    if (chosen.tb && promoState(chosen.h).dismissedAt) showTab(chosen);
    installTrigger(chosen);
  }

  if ('requestIdleCallback' in window) {
    requestIdleCallback(boot, { timeout: 2500 });
  } else {
    setTimeout(boot, 250);
  }
})();
